import { CATEGORIES, type Category } from './constants';
import type { Item } from './types';

export type View = 'open' | 'bought';
export type SortKey = 'new' | 'prio' | 'asc' | 'desc';
export const ALL = 'Alle';
export type CategoryFilter = Category | typeof ALL;

export const SORT_LABELS: Record<SortKey, string> = {
  new: 'Neueste zuerst',
  prio: 'Wichtigste zuerst',
  asc: 'Günstigste zuerst',
  desc: 'Teuerste zuerst',
};

type ListItem = Pick<Item, 'bought' | 'category' | 'price_cents' | 'priority' | 'created'>;

const inView = (view: View) => (i: ListItem) => (view === 'open' ? !i.bought : i.bought);
const time = (i: ListItem) => Date.parse(i.created.replace(' ', 'T')) || 0;

const sorters: Record<SortKey, (a: ListItem, b: ListItem) => number> = {
  new: (a, b) => time(b) - time(a),
  asc: (a, b) => a.price_cents - b.price_cents,
  desc: (a, b) => b.price_cents - a.price_cents,
  prio: (a, b) => a.priority - b.priority || time(b) - time(a),
};

/** Category chips for the current view: "Alle" plus each category that has items, with counts. */
export function categoryChips(items: ListItem[], view: View): { cat: CategoryFilter; count: number }[] {
  const counts = new Map<Category, number>();
  for (const i of items.filter(inView(view))) counts.set(i.category, (counts.get(i.category) ?? 0) + 1);
  return [
    { cat: ALL, count: [...counts.values()].reduce((a, b) => a + b, 0) },
    ...CATEGORIES.filter((c) => counts.has(c)).map((cat) => ({ cat, count: counts.get(cat)! })),
  ];
}

export function visibleItems<T extends ListItem>(
  items: T[],
  opts: { view: View; cat: CategoryFilter; sort: SortKey },
): T[] {
  return items
    .filter(inView(opts.view))
    .filter((i) => opts.cat === ALL || i.category === opts.cat)
    .sort(sorters[opts.sort]);
}
